import apiClient from './client';

// Survey types
export type FeedbackSurveyType = 'post_session' | 'midpoint' | 'endpoint';
// 'configured' = form/template saved but not scheduled to send
export type SurveyScheduleStatus = 'configured' | 'scheduled' | 'sent' | 'cancelled';
// Target audience for feedback surveys (MED-330)
export type TargetAudienceType = 'participants' | 'coaches' | 'both';
// Modality type for per-modality post-session survey config (MED-572)
export type SurveyModalityType = 'group_coaching' | 'workshop';

// Paperform form from API
export interface PaperformForm {
  id: string;
  title: string;
  slug: string;
  url: string;
  status: 'active' | 'draft' | 'archived';
  created_at: string;
  updated_at: string;
}

// Merge tag definition
export interface MergeTag {
  tag: string;
  description: string;
}

// Default email template
export interface EmailTemplate {
  subject: string;
  body: string;
}

// Survey config from API
export interface FeedbackSurveyConfig {
  id: string;
  cohort_id: string;
  survey_type: FeedbackSurveyType;
  target_audience: TargetAudienceType;
  modality_type: SurveyModalityType;
  paperform_id: string | null;
  email_subject: string | null;
  email_template: string | null;
  survey_deadline: string | null;
  schedule_status: SurveyScheduleStatus;
  scheduled_for: string | null;
  sent_at: string | null;
  auto_send: boolean;
  configured_by: string | null;
  configured_at: string;
  form_url: string | null;
  cohort_name?: string;
  configured_by_name?: string;
}

// Survey question from API (MED-330)
export interface FeedbackSurveyQuestion {
  id: string;
  survey_config_id: string;
  question_text: string;
  question_type: 'scale' | 'text';
  comparison_pair: 'A' | 'B' | 'C' | 'D' | null;
  display_order: number;
  is_required: boolean;
  scale_min: number | null;
  scale_max: number | null;
}

// Survey status summary
export interface SurveyStatusSummary {
  survey_type: FeedbackSurveyType;
  is_configured: boolean;
  schedule_status: SurveyScheduleStatus | null;
}

// Schedule times response
export interface ScheduleTimesResponse {
  midpoint: {
    session_number: number;
    session_end: string;
    scheduled_for: string;
  } | null;
  endpoint: {
    session_number: number;
    session_end: string;
    scheduled_for: string;
  } | null;
  total_sessions: number;
}

// Get config response (may include defaults if not configured)
export interface GetConfigResponse {
  config: FeedbackSurveyConfig | null;
  defaults?: EmailTemplate;
}

// Upsert request
export interface UpsertSurveyConfigRequest {
  paperform_id: string;
  email_subject: string;
  email_template: string;
  survey_deadline?: string | null;
  auto_send?: boolean;
  modality_type?: SurveyModalityType;
}

// Email preview response
export interface EmailPreviewResponse {
  preview: {
    subject: string;
    body: string;
  };
  sample_participant: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

// === Survey Results Types (MED-100) ===

/** Summary of survey results for the callout banner */
export interface SurveyResultsSummary {
  total: number;
  by_type: {
    post_session: number;
    midpoint: number;
    endpoint: number;
  };
  has_results: boolean;
}

/** Status of a survey line item within a group session table */
export interface SurveyLineItemStatus {
  survey_type: string;
  is_configured: boolean;
  schedule_status: string | null;
  sent_at: string | null;
  deadline: string | null;
  completed: number;
  total: number;
  state: 'collecting' | 'complete' | 'future' | 'not_configured';
  scheduled_for: string | null;
  avg_satisfaction: number | null;
}

/** Respondent attribution info */
export interface RespondentInfo {
  name: string;
  group: string | null;
}

/** A single response option with its count */
export interface ResponseDistribution {
  label: string;
  count: number;
  respondents?: RespondentInfo[];
}

/** A free-form text response with attribution */
export interface TextResponse {
  text: string;
  respondent_name: string | null;
  group_name: string | null;
}

/** Aggregated results for a single question */
export interface SurveyQuestion {
  question_id: string;
  question_text: string;
  question_type: string;
  question_category: 'scale' | 'text';
  responses: ResponseDistribution[];
  text_responses?: TextResponse[];
  total_responses: number;
  satisfaction_percentage: number | null;
}

/** A section grouping of questions */
export interface SurveySection {
  section_name: string;
  questions: SurveyQuestion[];
}

/** Full aggregated survey results */
export interface AggregatedSurveyResults {
  form_type: string;
  survey_type: string;
  total_submissions: number;
  total_participants: number;
  sections: SurveySection[];
}

// Coach feedback submission (MED-330)
export interface CoachFeedbackSubmission {
  id: string;
  coach_user_id: string;
  coach_name: string;
  submitted_at: string;
  parsed_data: Record<string, unknown>;
}

// Coach feedback response data for CM review modal (MED-330)
export interface CoachFeedbackResponse {
  config: {
    id: string;
    survey_type: string;
    schedule_status: string;
    survey_deadline: string | null;
    sent_at: string | null;
  } | null;
  questions: FeedbackSurveyQuestion[];
  submissions: CoachFeedbackSubmission[];
}

export const feedbackSurveysApi = {
  /**
   * Get all survey configs for a cohort
   */
  async getConfigs(cohortId: string): Promise<FeedbackSurveyConfig[]> {
    return apiClient.get<FeedbackSurveyConfig[]>(`/api/cohorts/${cohortId}/feedback-surveys`);
  },

  /**
   * Get survey status summary for a cohort
   */
  async getStatus(cohortId: string): Promise<{ status: SurveyStatusSummary[]; participant_count: number }> {
    return apiClient.get(`/api/cohorts/${cohortId}/feedback-surveys/status`);
  },

  /**
   * Get calculated schedule times for midpoint/endpoint
   */
  async getScheduleTimes(cohortId: string): Promise<ScheduleTimesResponse> {
    return apiClient.get<ScheduleTimesResponse>(`/api/cohorts/${cohortId}/feedback-surveys/schedule-times`);
  },

  /**
   * Get a specific survey config by type, with optional modality for post_session (MED-572)
   */
  async getConfigByType(
    cohortId: string,
    surveyType: FeedbackSurveyType,
    modalityType?: SurveyModalityType,
  ): Promise<GetConfigResponse> {
    const params = modalityType && surveyType === 'post_session'
      ? `?modality_type=${modalityType}`
      : '';
    return apiClient.get<GetConfigResponse>(`/api/cohorts/${cohortId}/feedback-surveys/${surveyType}${params}`);
  },

  /**
   * Create or update a survey config
   */
  async upsertConfig(
    cohortId: string,
    surveyType: FeedbackSurveyType,
    data: UpsertSurveyConfigRequest
  ): Promise<FeedbackSurveyConfig> {
    return apiClient.post<FeedbackSurveyConfig>(
      `/api/cohorts/${cohortId}/feedback-surveys/${surveyType}`,
      data
    );
  },

  /**
   * Schedule a midpoint/endpoint survey
   */
  async scheduleSurvey(
    cohortId: string,
    surveyType: 'midpoint' | 'endpoint',
    surveyDeadline?: string | null
  ): Promise<FeedbackSurveyConfig> {
    return apiClient.post<FeedbackSurveyConfig>(
      `/api/cohorts/${cohortId}/feedback-surveys/${surveyType}/schedule`,
      { survey_deadline: surveyDeadline }
    );
  },

  /**
   * Cancel a scheduled survey
   */
  async cancelSchedule(
    cohortId: string,
    surveyType: FeedbackSurveyType
  ): Promise<FeedbackSurveyConfig> {
    return apiClient.delete<FeedbackSurveyConfig>(
      `/api/cohorts/${cohortId}/feedback-surveys/${surveyType}/schedule`
    );
  },

  /**
   * Generate email preview with sample data
   */
  async getPreview(
    cohortId: string,
    surveyType: FeedbackSurveyType,
    data?: { email_subject?: string; email_template?: string; survey_deadline?: string }
  ): Promise<EmailPreviewResponse> {
    return apiClient.post<EmailPreviewResponse>(
      `/api/cohorts/${cohortId}/feedback-surveys/${surveyType}/preview`,
      data || {}
    );
  },

  /**
   * Delete a survey config, with optional modality for post_session (MED-572)
   */
  async deleteConfig(
    cohortId: string,
    surveyType: FeedbackSurveyType,
    modalityType?: SurveyModalityType,
  ): Promise<void> {
    const params = modalityType && surveyType === 'post_session'
      ? `?modality_type=${modalityType}`
      : '';
    await apiClient.delete(`/api/cohorts/${cohortId}/feedback-surveys/${surveyType}${params}`);
  },

  /**
   * Get available merge tags
   */
  async getMergeTags(): Promise<MergeTag[]> {
    return apiClient.get<MergeTag[]>('/api/feedback-surveys/merge-tags');
  },

  /**
   * Get default email templates
   */
  async getDefaultTemplates(): Promise<Record<FeedbackSurveyType, EmailTemplate>> {
    return apiClient.get('/api/feedback-surveys/templates');
  },

  /**
   * Get available Paperform forms
   */
  async getPaperforms(): Promise<PaperformForm[]> {
    return apiClient.get<PaperformForm[]>('/api/feedback-surveys/paperforms');
  },

  // === Survey Results Methods (MED-100) ===

  /**
   * Get survey results summary for callout banner
   */
  async getResultsSummary(cohortId: string): Promise<SurveyResultsSummary> {
    return apiClient.get<SurveyResultsSummary>(
      `/api/cohorts/${cohortId}/feedback-surveys/results/summary`,
    );
  },

  /**
   * Get line item status for a group's survey
   */
  async getLineItemStatus(
    cohortId: string,
    groupId: string,
    surveyType: string,
  ): Promise<SurveyLineItemStatus> {
    return apiClient.get<SurveyLineItemStatus>(
      `/api/cohorts/${cohortId}/feedback-surveys/results/line-item?group_id=${groupId}&survey_type=${surveyType}`,
    );
  },

  /**
   * Get aggregated survey results for visualization
   */
  async getAggregatedResults(
    cohortId: string,
    formType: string,
    groupId?: string,
    sessionId?: string,
  ): Promise<AggregatedSurveyResults> {
    const params = new URLSearchParams({ form_type: formType });
    if (groupId) params.set('group_id', groupId);
    if (sessionId) params.set('session_id', sessionId);
    return apiClient.get<AggregatedSurveyResults>(
      `/api/cohorts/${cohortId}/feedback-surveys/results?${params.toString()}`,
    );
  },

  /**
   * Get coach feedback submissions for CM review modal (MED-330)
   */
  async getCoachResponses(
    cohortId: string,
    surveyType: 'midpoint' | 'endpoint',
  ): Promise<CoachFeedbackResponse> {
    return apiClient.get<CoachFeedbackResponse>(
      `/api/cohorts/${cohortId}/feedback-surveys/coach-responses/${surveyType}`,
    );
  },
};
